


/* ========================================================================== */
/*  FILE: components/home/HowItWorksClient.tsx                                 */
/* ========================================================================== */

"use client";

import React, { useMemo } from "react";
import {
  ArrowRight,
  Calendar,
  Clock,
  CreditCard,
  QrCode,
  Search,
  Shield,
  Sparkles,
  UtensilsCrossed,
  Zap,
} from "lucide-react";
import Link from "next/link";

type Step = {
  number: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  accentText: string;
  accentBg: string;
};

export type HowItWorksClientProps = {
  heading?: string;
  subheading?: string;
  steps?: Step[];
  showPaymentMethods?: boolean;
  paymentMethods?: Array<{ label: string; accent: string; initials: string }>;
  ctaHref?: string;
  ctaLabel?: string;
};

function cn(...classes: Array<string | undefined | null | false>) {
  return classes.filter(Boolean).join(" ");
}

export default function HowItWorksClient(props: HowItWorksClientProps) {
  const steps: Step[] = useMemo(
    () =>
      props.steps ?? [
        {
          number: "1",
          title: "Discover",
          description: "Browse restaurants by cuisine, rating, location, and offers.",
          icon: Search,
          accentText: "text-sky-700",
          accentBg: "bg-sky-50",
        },
        {
          number: "2",
          title: "Order & Book",
          description: "Order ahead, scan QR, or reserve tables with instant confirmation.",
          icon: UtensilsCrossed,
          accentText: "text-amber-700",
          accentBg: "bg-amber-50",
        },
        {
          number: "3",
          title: "Track",
          description: "Follow your order status with clear, real-time updates.",
          icon: Clock,
          accentText: "text-emerald-700",
          accentBg: "bg-emerald-50",
        },
        {
          number: "4",
          title: "Enjoy & Pay",
          description: "Pay securely with multiple methods and keep receipts in one place.",
          icon: CreditCard,
          accentText: "text-violet-700",
          accentBg: "bg-violet-50",
        },
      ],
    [props.steps]
  );

  const heading = props.heading ?? "How It Works";
  const subheading =
    props.subheading ??
    "Four simple steps to your perfect dining experience — built for speed and clarity.";

  const paymentMethods =
    props.paymentMethods ?? [
      { label: "IndePay", accent: "bg-sky-500", initials: "I" },
      { label: "Airtel Money", accent: "bg-red-500", initials: "A" },
      { label: "TNM Mpamba", accent: "bg-green-500", initials: "T" },
      { label: "Visa/Mastercard", accent: "bg-violet-500", initials: "V" },
      { label: "Cash", accent: "bg-amber-500", initials: "K" },
    ];

  const ctaHref = props.ctaHref ?? "/vendors";
  const ctaLabel = props.ctaLabel ?? "Start Exploring";

  return (
    <section className="relative py-18 sm:py-20 bg-gradient-to-b from-neutral-50 to-white" aria-label="How it works">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(251,191,36,0.08),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_40%,rgba(249,115,22,0.08),transparent_60%)]" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 mb-4 rounded-full border border-neutral-200 bg-white/80 px-4 py-2 backdrop-blur shadow-sm">
            <Zap className="h-4 w-4 text-amber-600" />
            <span className="text-xs sm:text-sm font-semibold text-amber-700 uppercase tracking-wide">
              Simple & easy
            </span>
            <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] text-neutral-600 border border-neutral-200">
              <Sparkles className="h-3.5 w-3.5 text-amber-500" />
              Smooth flow
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-neutral-900">
            {heading}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-neutral-600 leading-relaxed">
            {subheading}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8">
          {steps.map((step, idx) => {
            const Icon = step.icon;
            return (
              <div key={`${step.title}-${idx}`} className="relative">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <div
                    className={cn(
                      "h-10 w-10 rounded-full flex items-center justify-center font-extrabold text-lg border border-neutral-200",
                      step.accentBg,
                      step.accentText
                    )}
                  >
                    {step.number}
                  </div>
                </div>

                <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm p-7 sm:p-8 pt-10 h-full">
                  <div className={cn("h-14 w-14 rounded-2xl flex items-center justify-center", step.accentBg)}>
                    <Icon className={cn("h-7 w-7", step.accentText)} />
                  </div>
                  <h3 className="mt-6 text-xl font-extrabold text-neutral-900">
                    {step.title}
                  </h3>
                  <p className="mt-3 text-neutral-600 leading-relaxed">
                    {step.description}
                  </p>

                  <div className="mt-7 h-px bg-gradient-to-r from-transparent via-neutral-200 to-transparent" />

                  <div className="mt-5 flex items-center justify-between text-sm text-neutral-500">
                    <span>Step {idx + 1}</span>
                    <span className="inline-flex items-center gap-2">
                      {step.title === "Order & Book" ? <Calendar className="h-4 w-4" /> : null}
                      {step.title === "Discover" ? <Search className="h-4 w-4" /> : null}
                      {step.title === "Track" ? <Clock className="h-4 w-4" /> : null}
                      {step.title === "Enjoy & Pay" ? <CreditCard className="h-4 w-4" /> : null}
                    </span>
                  </div>
                </div>

                {idx < steps.length - 1 ? (
                  <div className="hidden xl:block absolute top-6 right-0 translate-x-1/2 w-12 h-0.5 bg-neutral-200" />
                ) : null}
              </div>
            );
          })}
        </div>

        {props.showPaymentMethods !== false && (
          <div className="mt-16 text-center">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-neutral-900">
              Supported Payment Methods
            </h3>
            <p className="mt-3 text-neutral-600 max-w-2xl mx-auto">
              Choose the method that works best for you — quick checkout with secure flows.
            </p>

            <div className="mt-8 flex flex-wrap justify-center items-center gap-4">
              {paymentMethods.map((m) => (
                <div
                  key={m.label}
                  className="inline-flex items-center gap-3 rounded-2xl border border-neutral-200 bg-white px-5 py-3 shadow-sm"
                >
                  <div className={cn("h-9 w-9 rounded-full flex items-center justify-center text-white font-extrabold", m.accent)}>
                    {m.initials}
                  </div>
                  <span className="font-bold text-neutral-800">{m.label}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href={ctaHref}
                className={cn(
                  "group inline-flex items-center justify-center gap-3 rounded-2xl px-8 py-4",
                  "bg-neutral-900 text-white font-extrabold shadow-xl hover:bg-neutral-800",
                  "transition-transform duration-200 hover:scale-[1.01] active:scale-[0.98]"
                )}
              >
                <span>{ctaLabel}</span>
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>

              <Link
                href="/vendors"
                className={cn(
                  "group inline-flex items-center justify-center gap-3 rounded-2xl px-8 py-4",
                  "bg-white text-neutral-900 font-extrabold border border-neutral-200 shadow-sm hover:shadow-md",
                  "transition-transform duration-200 hover:scale-[1.01] active:scale-[0.98]"
                )}
              >
                <QrCode className="h-5 w-5" />
                Scan QR
              </Link>
            </div>

            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white/80 px-4 py-2 text-xs text-neutral-600">
              <Shield className="h-4 w-4 text-amber-600" />
              Payments and sessions are protected with modern security standards.
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
